// src/auth/useIsAdmin.js
//
// Client-side "is the current user an admin?" hook for READ-ONLY VIEW decisions
// (letting an admin load a pack's paid content without an entitlement -- see
// adminView.js). Mirrors AdminGuard's authority EXACTLY: the `role === 'admin'`
// custom claim via getIdTokenClaims (mock mode -> admin for local dev only). This
// is NOT a security boundary (AG18 / locked #13): firestore.rules + Cloud Functions
// independently enforce admin. Returns a boolean (false until the async claim
// resolves; fail-closed on error).

import { useEffect, useState } from 'react';
import { useAuth } from './useAuth.js';
import { roleGrantsAdminView } from './adminView.js';

export function useIsAdmin() {
    const { currentUser, loading, getIdTokenClaims, authMode } = useAuth();
    const [isAdmin, setIsAdmin] = useState(false);

    useEffect(() => {
        if (loading) return;
        if (!currentUser) { setIsAdmin(false); return; }
        // Mock mode has no backend / custom claims; AdminGuard grants admin to any
        // signed-in mock user for local demo (readers are inert in mock -- no
        // Firestore -- so this never affects a real paid read).
        if (authMode === 'mock') { setIsAdmin(true); return; }

        // Cancellation-safe (P7): reset on user change, ignore a resolved promise
        // after unmount / user switch.
        let cancelled = false;
        setIsAdmin(false);
        getIdTokenClaims()
            .then((claims) => { if (!cancelled) setIsAdmin(roleGrantsAdminView(claims)); })
            .catch((err) => { if (!cancelled) { console.error('useIsAdmin claims fetch failed:', err); setIsAdmin(false); } });
        return () => { cancelled = true; };
    }, [currentUser, loading, authMode, getIdTokenClaims]);

    return isAdmin;
}
